import { FormikProps } from "formik";
import { useState } from "react";
import { ICCheckbox, ICTextInput } from "../../core-component/form";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import { FEATURE_ANNOTATION_MAX_LENGTH } from "../../utils/PurchaseConstants";
import { CONFIG_VALUE_MODE_ENUM } from "../../utils/PurchaseEnumerations";
import {
  VALUE_FEATURE_CONFIG_KEY,
  IAvailableFeature,
  IPackageEditorForm,
} from "./PackageEditorController";

interface IPackageFeaturesTabProps {
  formikProps: FormikProps<IPackageEditorForm>;
  availableFeatures: IAvailableFeature[];
  readOnly: boolean;
}

const PackageFeaturesTab: React.FC<IPackageFeaturesTabProps> = ({
  formikProps,
  availableFeatures,
  readOnly,
}) => {
  const { t } = UseTranslationHook();
  const { values, setFieldValue } = formikProps;
  const [search, setSearch] = useState("");

  const searchText = search.trim().toLowerCase();
  const visibleFeatures = availableFeatures.filter((feature) =>
    feature.feature_title.toLowerCase().includes(searchText),
  );
  const includedCount = Object.values(values.features).filter((feature) => feature.included).length;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <ICTextInput
            type="search"
            placeholder={t("search_features")}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <span className="text-sm text-muted whitespace-nowrap">
          {includedCount} / {availableFeatures.length}
        </span>
      </div>
      <div className="divide-y">
        {visibleFeatures.map((feature) => {
          const value = values.features[feature.id] ?? { included: false, annotation: "" };
          const valueKey = VALUE_FEATURE_CONFIG_KEY[feature.feature_title];
          const isValueLine = valueKey !== undefined;
          const isProvided =
            isValueLine &&
            (values.configs[valueKey]?.mode ?? CONFIG_VALUE_MODE_ENUM.NotProvided) !==
              CONFIG_VALUE_MODE_ENUM.NotProvided;
          return (
            <div key={feature.id} className="grid grid-cols-5 gap-3 items-center py-2">
              <div className="col-span-3" title={feature.help_text ?? ""}>
                <ICCheckbox
                  label={feature.feature_title}
                  checked={isValueLine ? isProvided : value.included}
                  disabled={readOnly || isValueLine}
                  onChange={(checked) =>
                    setFieldValue(`features.${feature.id}`, { ...value, included: checked })
                  }
                />
              </div>
              <div className="col-span-2">
                {isValueLine ? (
                  <span className="text-xs text-muted">{t("credit_feature_auto")}</span>
                ) : (
                  <ICTextInput
                    placeholder={t("annotation")}
                    value={value.annotation}
                    maxLength={FEATURE_ANNOTATION_MAX_LENGTH}
                    disabled={readOnly || !value.included}
                    onChange={(event) =>
                      setFieldValue(`features.${feature.id}`, {
                        ...value,
                        annotation: event.target.value,
                      })
                    }
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PackageFeaturesTab;
